import { useEffect } from 'react';
import {
  CurrencyType,
  FulfillmentOrdersResponseDto,
  OrderDetailsAmountsDto,
  RefundAttemptDto,
  TransactionChargesResponseDto,
} from '@tiendanube/common';
import { DataList, Text } from '@tiendanube/components';
import FeatureFlag from 'App/components/FeatureFlag';
import {
  TOTAL_PAID_ORDER_DETAIL,
  PAYMENTS_MERCHANT_CHARGES_TAG,
} from 'App/featuresFlags';
import { CurrencyPrice } from 'commons/components';
import { useBusinessData } from 'domains/Account/hooks';
import {
  OrderTotalAmountRow,
  OrderDiscountAmountRow,
  OrderMerchantChargeAmountRow,
  OrderRefundAmountRow,
  OrderRefundedMerchantChargeRow,
  OrderSubtotalAmountRow,
} from 'domains/Orders/components';
import { useIsEditOrdersEnabled } from 'domains/Orders/Orders/hooks';
import {
  useOrderDiscounts,
  useNuvemPagoRedirect,
} from 'domains/Orders/Orders/pages/hooks';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import ShippingCosts from './components/ShippingCosts';
import { useHasTags } from '../../../../../../../../App/hooks';

interface OrderSummaryAmountsProps {
  amounts: OrderDetailsAmountsDto;
  refundAttempts: RefundAttemptDto[];
  currency: CurrencyType;
  paymentMethod: string;
  fulfillmentOrders?: FulfillmentOrdersResponseDto[];
  totalProducts: number;
  isEdited?: boolean;
  charges: TransactionChargesResponseDto;
  gateway?: string;
}

function CardPaymentAmounts({
  amounts,
  refundAttempts,
  currency,
  paymentMethod,
  fulfillmentOrders,
  totalProducts,
  isEdited,
  charges,
  gateway,
}: OrderSummaryAmountsProps): JSX.Element {
  const {
    subtotal,
    total,
    shipping,
    previousShippingOwner,
    shippingDetail,
    totalPaid,
    otherCosts,
    paidByCustomer,
    totalDiffPending,
  } = amounts;

  const t = useTranslationOrders();
  const hasEditOrders = useIsEditOrdersEnabled();
  const { fetchBusinessData } = useBusinessData();

  const discounts = useOrderDiscounts(amounts, paymentMethod);
  const hasSuccessfulRefund = refundAttempts.some(
    (attempt) => attempt.status.code === 'success',
  );
  const hasMerchantCharges = charges.merchantCharges?.length > 0;
  const hasRefundedMerchantCharges =
    Array.isArray(charges.refundedMerchantCharges) &&
    charges.refundedMerchantCharges?.length > 0;
  const [hasMerchantChargesTag] = useHasTags(PAYMENTS_MERCHANT_CHARGES_TAG);
  const { isNuvemPagoPayment } = useNuvemPagoRedirect(gateway ?? '');
  const showMerchantCharges = hasMerchantChargesTag && isNuvemPagoPayment();

  useEffect(() => {
    fetchBusinessData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isTotalUpdated = () =>
    (isEdited && total !== paidByCustomer && paidByCustomer > 0) || false;

  const normalizedShipping = shipping ?? 0;
  return (
    <>
      <DataList.Row grayed id="subtotal">
        <DataList.Cell width={70}>
          <Text>{`${t('orderSummaryCard.tableAmounts.subtotal', {
            count: totalProducts,
          })}`}</Text>
        </DataList.Cell>
        <DataList.Cell width={30}>
          <CurrencyPrice
            price={subtotal}
            currency={currency}
            block
            textAlign="right"
          />
        </DataList.Cell>
      </DataList.Row>
      {(normalizedShipping >= 0 || !!fulfillmentOrders) && (
        <ShippingCosts
          shippingDetail={shippingDetail}
          shipping={normalizedShipping}
          previousShipping={previousShippingOwner}
          currency={currency}
          fulfillmentOrders={fulfillmentOrders}
        />
      )}

      {discounts.map((discount) => (
        <OrderDiscountAmountRow
          key={discount.id}
          discount={discount}
          currency={currency}
        />
      ))}

      {!showMerchantCharges && (
        <>
          {!isEdited && (
            <>
              {hasSuccessfulRefund && (
                <OrderSubtotalAmountRow currency={currency} total={total} />
              )}
              {hasSuccessfulRefund && (
                <OrderRefundAmountRow
                  refundAttempts={refundAttempts}
                  currency={currency}
                />
              )}
            </>
          )}

          <OrderTotalAmountRow
            isTotalUpdated={isTotalUpdated()}
            total={total}
            refundAttempts={refundAttempts}
            currency={currency}
          />
        </>
      )}

      {showMerchantCharges && (
        <>
          {!isEdited && (
            <>
              {(hasSuccessfulRefund ||
                hasMerchantCharges ||
                hasRefundedMerchantCharges) && (
                <OrderSubtotalAmountRow currency={currency} total={total} />
              )}
              {hasSuccessfulRefund && (
                <OrderRefundAmountRow
                  refundAttempts={refundAttempts}
                  currency={currency}
                />
              )}

              {(charges.merchantCharges ?? []).map((charge) => (
                <OrderMerchantChargeAmountRow
                  key={charge.type}
                  transactionCharge={charge}
                />
              ))}

              {Array.isArray(charges.refundedMerchantCharges) &&
                charges.refundedMerchantCharges.length > 0 && (
                  // provisional for version 1
                  <OrderRefundedMerchantChargeRow
                    refundedMerchantChargesTotal={
                      charges.refundedMerchantChargesTotal
                    }
                    defaultCurrency={currency}
                  />
                )}
            </>
          )}

          <OrderTotalAmountRow
            isTotalUpdated={isTotalUpdated()}
            total={total}
            refundAttempts={refundAttempts}
            currency={currency}
            merchantChargesTotalAmount={charges.merchantChargesTotal}
            refundedMerchantChargesTotalAmount={
              charges.refundedMerchantChargesTotal
            }
          />
        </>
      )}

      {hasEditOrders && isEdited && (
        <>
          <DataList.Row id="paidBycustomer">
            <DataList.Cell width={70}>
              <Text>{t('orderSummaryCard.tableAmounts.paidBycustomer')}</Text>
            </DataList.Cell>
            <DataList.Cell width={30}>
              <CurrencyPrice
                price={paidByCustomer}
                currency={currency}
                block
                textAlign="right"
              />
            </DataList.Cell>
          </DataList.Row>

          <DataList.Row id="totalDiff" grayed>
            <DataList.Cell width={70}>
              <Text bold>
                {totalDiffPending < 0
                  ? t('orderSummaryCard.tableAmounts.feeToPay')
                  : t('orderSummaryCard.tableAmounts.feeToCharge')}
              </Text>
            </DataList.Cell>
            <DataList.Cell width={30}>
              <CurrencyPrice
                price={Math.abs(totalDiffPending)}
                currency={currency}
                block
                textAlign="right"
                bold
              />
            </DataList.Cell>
          </DataList.Row>
        </>
      )}

      <FeatureFlag flag={TOTAL_PAID_ORDER_DETAIL}>
        {!!totalPaid && !!otherCosts && (
          <>
            <DataList.Row id="otherCosts">
              <DataList.Cell width={70}>
                <Text>{`${t(
                  'orderSummaryCard.tableAmounts.otherCosts',
                )}`}</Text>
              </DataList.Cell>
              <DataList.Cell width={30}>
                <CurrencyPrice
                  price={otherCosts}
                  currency={currency}
                  block
                  textAlign="right"
                />
              </DataList.Cell>
            </DataList.Row>

            <DataList.Row id="totalPaid" grayed>
              <DataList.Cell width={70}>
                <Text bold>{`${t(
                  'orderSummaryCard.tableAmounts.totalPaid',
                )}`}</Text>
              </DataList.Cell>
              <DataList.Cell width={30}>
                <CurrencyPrice
                  price={totalPaid}
                  currency={currency}
                  block
                  textAlign="right"
                  bold
                />
              </DataList.Cell>
            </DataList.Row>
          </>
        )}
      </FeatureFlag>
    </>
  );
}
export default CardPaymentAmounts;
